import type { Logger as PinoLogger } from 'pino';
import type { Logger, LoggerWithChild, HyperLogger } from './types';

export function createHyperLogger(sdkLogger: any, sdk: any): HyperLogger {
	return {
		info: (message: any) => {
			const msg = typeof message === 'string' ? message : JSON.stringify(message);
			console.info(msg);
			sdkLogger.info(msg);
		},
		warn: (message: any) => {
			const msg = typeof message === 'string' ? message : JSON.stringify(message);
			console.warn(msg);
			sdkLogger.warn(msg);
		},
		error: (message: any) => {
			const msg = typeof message === 'string' ? message : JSON.stringify(message);
			console.error(msg);
			sdkLogger.error(msg);
		},
		debug: (message: any) => {
			const msg = typeof message === 'string' ? message : JSON.stringify(message);
			console.debug(msg);
			sdkLogger.debug(msg);
		},
		fetch: (input: RequestInfo | URL, init?: RequestInit) => sdk.fetch(input, init),
	};
}


export function hyperLoggerWrapper(hyperLogger: HyperLogger): Logger {
	return {
		trace: (message, attributes) => {
			hyperLogger.info({
				message,
				...attributes,
				level: 'trace',
			});
		},
		debug: (message, attributes) => {
			hyperLogger.debug({
				message,
				...attributes,
			});
		},
		info: (message, attributes) => {
			hyperLogger.info({
				message,
				...attributes,
			});
		},
		warn: (message, attributes) => {
			hyperLogger.warn({
				message,
				...attributes,
			});
		},
		error: (message, attributes) => {
			hyperLogger.error({
				message,
				...attributes,
			});
		},
		fatal: (message, attributes) => {
			hyperLogger.error({
				message,
				...attributes,
				level: 'fatal',
			});
		},
	};
}

//* Wrapper for pino logger
export function pinoWrapper(pino: PinoLogger): Logger {
	return {
		trace: (message, attributes) => {
			if (attributes == null) {
				pino.trace(message);
				return;
			}

			pino.trace(attributes, message);
		},
		debug: (message, attributes) => {
			if (attributes == null) {
				pino.debug(message);
				return;
			}

			pino.debug(attributes, message);
		},
		info: (message, attributes) => {
			if (attributes == null) {
				pino.info(message);
				return;
			}
			pino.info(attributes, message);
		},
		warn: (message, attributes) => {
			if (attributes == null) {
				pino.warn(message);
				return;
			}

			pino.warn(attributes, message);
		},
		error: (message, attributes) => {
			if (attributes == null) {
				pino.error(message);
				return;
			}

			pino.error(attributes, message);
		},
		fatal: (message, attributes) => {
			if (attributes == null) {
				pino.fatal(message);
				return;
			}

			pino.fatal(attributes, message);
		},
	};
}

//* Wrapper for sentry logger, to build the attributes object
export function sentryWrapper(sentry: Logger): Logger {
	//* convert the attributes object to sentry compatible object
	function convertAttributes(
		obj?: Record<string, any>,
		parentKey: string = '',
		result: Record<string, any> = {}
	): Record<string, any> | undefined {
		if (obj == null || Object.keys(obj).length === 0) {
			return undefined;
		}
		for (const [key, value] of Object.entries(obj)) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;

			if (value instanceof Error) {
				//* Native Error objects have no enumerable own properties, so a plain
				//* recursion/spread here would drop message and stack, sending {} to Sentry.
				result[`${newKey}.message`] = value.message;
				if (value.name) result[`${newKey}.name`] = value.name;
				if (value.stack) result[`${newKey}.stack`] = value.stack;
				//* Error subclasses (e.g. third-party SDK errors) can carry extra enumerable
				//* own properties (code, statusCode, etc.) - preserve those too.
				convertAttributes(value as unknown as Record<string, any>, newKey, result);
				continue;
			}

			if (value && typeof value === 'object' && !Array.isArray(value)) {
				//* Recursion for objects
				convertAttributes(value, newKey, result);
			} else {
				//* Assign primitive or array directly
				result[newKey] = value;
			}
		}
		return result;
	}
	return {
		trace: (message, attributes) => {
			sentry.trace(message, convertAttributes(attributes));
		},
		debug: (message, attributes) => {
			sentry.debug(message, convertAttributes(attributes));
		},
		info: (message, attributes) => {
			sentry.info(message, convertAttributes(attributes));
		},
		warn: (message, attributes) => {
			sentry.warn(message, convertAttributes(attributes));
		},
		error: (message, attributes) => {
			sentry.error(message, convertAttributes(attributes));
		},
		fatal: (message, attributes) => {
			sentry.fatal(message, convertAttributes(attributes));
		},
	};
}
//* fanout logger to send the log to multiple loggers
export function fanoutLogger({ loggers, bindings }: { loggers: Logger[]; bindings?: Record<string, unknown> }): LoggerWithChild {
	//* format the message to be sent to the loggers
	const formatMessage = (scope: string, functionName: string, message: string): string => {
		return `[${scope} - ${functionName}] - ${message}`;
	};

	return {
		trace: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.trace(formattedMessage, { props });
					continue;
				}
				logger.trace(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		debug: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.debug(formattedMessage, { props });
					continue;
				}
				logger.debug(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		info: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.info(formattedMessage, { props });
					continue;
				}
				logger.info(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		warn: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.warn(formattedMessage, { props });
					continue;
				}
				logger.warn(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		error: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.error(formattedMessage, { props });
					continue;
				}
				logger.error(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		fatal: ({ scope, functionName, message, props }) => {
			const formattedMessage = formatMessage(scope, functionName, message);
			for (const logger of loggers) {
				if (!bindings) {
					logger.fatal(formattedMessage, { props });
					continue;
				}
				logger.fatal(formattedMessage, {
					props: {
						...bindings,
						...props,
					},
				});
			}
		},
		child: (bindings) => {
			return fanoutLogger({ loggers, bindings: { ...bindings, ...bindings } });
		},
	};
}
